/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2023
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { AnyAction } from '@reduxjs/toolkit';
import _ from 'lodash';
import { combineReducers } from 'redux';

import { IntentsSlice } from '../../../../ts/state';
import {
  CLEAR_ACTION_START,
  GET_COUNTER_EXAMPLE_SUCCESS,
  GET_RELATED_INTENT_SUCCESS
} from '../actions/actionTypes';
import {
  ACTIONS_ADD_EXAMPLE,
  ACTIONS_ADD_MULTIPLE_COUNTER_EXAMPLES,
  ACTIONS_ADD_MULTIPLE_EXAMPLES,
  ACTIONS_DELETE_EXAMPLE,
  ACTIONS_GET_START
} from '../models/actions/actionTypes';
import * as types from './actionTypes';

// Array of intent IDs
const allIds = (
  state: IntentsSlice['allIds'] = [],
  action: AnyAction
): IntentsSlice['allIds'] => {
  switch (action.type) {
    case types.FETCH_INTENTS_START: {
      state = [];
      return state;
    }
    case types.FETCH_INTENTS_SUCCESS:
    case types.FILTER_INTENTS_SUCCESS:
    case types.IMPORT_INTENTS_SUCCESS:
    case types.REPLACE_INTENTS_SUCCESS:
      // expect a normalized input
      return action.response.result;
    case types.FETCH_MORE_INTENTS_SUCCESS: // APPEND the new intents
      return state.concat(action.response.result);
    case types.ADD_INTENT_SUCCESS:
      return [...state, action.response.result];
    case types.UPDATE_INTENT_UPDATE_SUCCESS:
      return [
        ...state.filter(value => value !== action.intent.intent),
        action.response.result
      ];
    case types.REMOVE_INTENTS_SUCCESS:
      return state.filter(id => action.intents.indexOf(id) < 0);
    case types.DELETE_ALL_INTENTS_SUCCESS:
    case types.CLEAR_ALL_IDS: {
      state = [];
      return state;
    }
    default:
      return state;
  }
};

const conflicts = (
  state: IntentsSlice['conflicts'] = [],
  action: AnyAction
): IntentsSlice['conflicts'] => {
  switch (action.type) {
    case types.FETCH_INTENT_CONFLICTS_SUCCESS:
      return action.response.result;
    default:
      return state;
  }
};

const INITIAL_EDITING: IntentsSlice['editing'] = {
  intent: null,
  id: ''
};

// Information about the currently edited intent
const editing = (
  state: IntentsSlice['editing'] = INITIAL_EDITING,
  action: AnyAction
): IntentsSlice['editing'] => {
  let updatedIntent;
  switch (action.type) {
    case types.UPDATE_INTENT_START:
      return {
        ...state,
        id: action.id,
        intent: action.intent
      };
    case types.ADD_INTENT_SUCCESS:
    case types.UPDATE_INTENT_UPDATE_SUCCESS:
    case types.UPDATE_INTENT_EXAMPLE_SUCCESS:
    case types.ADD_INTENT_EXAMPLE_SUCCESS:
    case types.ADD_EXAMPLES_SUCCESS:
    case types.REMOVE_INTENT_EXAMPLES_SUCCESS:
      return {
        ...state,
        id: action.intent.intent,
        intent: {
          ...state.intent,
          ...action.intent
        },
        timestamp: Date.now()
      };
    case types.MOVE_INTENT_EXAMPLES_SUCCESS:
      updatedIntent = _.find(action.changedIntents, intent => {
        return intent.intent === action.editedIntentName;
      });
      return {
        ...state,
        id: updatedIntent.intent,
        intent: updatedIntent
      };
    case types.UPDATE_INTENT_CANCEL:
      return {
        ...state,
        ...INITIAL_EDITING
      };
    default:
      return state;
  }
};

const INITIAL_EXAMPLES: IntentsSlice['examples'] = {
  loading: false,
  errorMessage: null,
  examples: [],
  counterExamples: [],
  intentName: ''
};

// fetch examples for specific intent
const examples = (
  state: IntentsSlice['examples'] = INITIAL_EXAMPLES,
  action: AnyAction
): IntentsSlice['examples'] => {
  switch (action.type) {
    case types.LIST_INTENT_EXAMPLES_START:
      return {
        ...state,
        loading: true,
        errorMessage: null,
        examples: [],
        intentName: action.intentName
      };
    case types.LIST_INTENT_EXAMPLES_SUCCESS:
    case GET_RELATED_INTENT_SUCCESS:
      return { ...state, loading: false, examples: action.examples };
    case ACTIONS_ADD_EXAMPLE:
      return {
        ...state,
        examples: state.examples.concat([action.newExampleText])
      };
    case ACTIONS_DELETE_EXAMPLE:
      return {
        ...state,
        examples: state.examples.filter(id => id !== action.exampleId)
      };
    case ACTIONS_ADD_MULTIPLE_EXAMPLES:
      return {
        ...state,
        examples: state.examples.concat(action.newExamplesTexts)
      };
    case GET_COUNTER_EXAMPLE_SUCCESS:
      return {
        ...state,
        loading: false,
        counterExamples: action.counterExamples
      };
    case ACTIONS_ADD_MULTIPLE_COUNTER_EXAMPLES:
      return {
        ...state,
        counterExamples: state.counterExamples.concat(action.newExamplesTexts)
      };
    case ACTIONS_GET_START:
    case CLEAR_ACTION_START:
      return { ...state, examples: [], counterExamples: [] };
    case types.LIST_INTENT_EXAMPLES_ERROR:
      return { ...state, loading: false, errorMessage: action.error };
    default:
      return state;
  }
};

const INITIAL_PROPS: IntentsSlice['props'] = {
  errorMessage: null,
  filterLoading: false,
  lastFetched: 0,
  loading: false,
  loadingMore: false
};

// UI specific information
const props = (
  state: IntentsSlice['props'] = INITIAL_PROPS,
  action: AnyAction
): IntentsSlice['props'] => {
  switch (action.type) {
    case types.FETCH_INTENTS_START:
    case types.REMOVE_INTENTS_START:
    case types.ADD_INTENT_START:
    case types.ADD_INTENT_EXAMPLE_START:
    case types.UPDATE_INTENT_EXAMPLE_START:
    case types.REMOVE_INTENT_EXAMPLES_START:
    case types.MOVE_INTENT_EXAMPLES_START:
    case types.UPDATE_INTENT_UPDATE_START:
    case types.FETCH_INTENT_CONFLICTS_START:
    case types.REPLACE_INTENTS_START:
    case types.DELETE_ALL_INTENTS_START:
      return { ...state, loading: true, errorMessage: null };
    case types.FETCH_MORE_INTENTS_START:
      return { ...state, loadingMore: true, errorMessage: null };
    case types.FILTER_INTENTS_START:
      return { ...state, filterLoading: true, errorMessage: null };
    case types.FETCH_INTENTS_SUCCESS:
      return { ...state, loading: false, lastFetched: Date.now() };
    case types.LIST_INTENT_EXAMPLES_SUCCESS:
    case types.ADD_INTENT_SUCCESS:
    case types.UPDATE_INTENT_UPDATE_SUCCESS:
    case types.REMOVE_INTENTS_SUCCESS:
    case types.ADD_EXAMPLES_SUCCESS:
    case types.ADD_INTENT_EXAMPLE_SUCCESS:
    case types.UPDATE_INTENT_EXAMPLE_SUCCESS:
    case types.REMOVE_INTENT_EXAMPLES_SUCCESS:
    case types.MOVE_INTENT_EXAMPLES_SUCCESS:
    case types.FETCH_INTENT_CONFLICTS_SUCCESS:
    case types.REPLACE_INTENTS_SUCCESS:
    case types.DELETE_ALL_INTENTS_SUCCESS:
      return { ...state, loading: false };
    case types.FETCH_MORE_INTENTS_SUCCESS:
      return { ...state, loadingMore: false };
    case types.FILTER_INTENTS_SUCCESS:
      return { ...state, filterLoading: false };
    case types.FETCH_INTENTS_ERROR:
    case types.ADD_INTENT_ERROR:
    case types.REMOVE_INTENTS_ERROR:
    case types.ADD_INTENT_EXAMPLE_ERROR:
    case types.UPDATE_INTENT_EXAMPLE_ERROR:
    case types.REMOVE_INTENT_EXAMPLES_ERROR:
    case types.MOVE_INTENT_EXAMPLES_ERROR:
    case types.IMPORT_INTENTS_ERROR:
    case types.ADD_EXAMPLES_ERROR:
    case types.FETCH_INTENT_CONFLICTS_ERROR:
    case types.REPLACE_INTENTS_ERROR:
    case types.DELETE_ALL_INTENTS_ERROR:
    case types.UPDATE_INTENT_UPDATE_ERROR:
      return { ...state, loading: false, errorMessage: action.error };
    case types.FETCH_MORE_INTENTS_ERROR:
      return { ...state, loadingMore: false, errorMessage: action.error };
    case types.FILTER_INTENTS_ERROR:
      return { ...state, filterLoading: false, errorMessage: action.error };
    default:
      return state;
  }
};

export const reducer = combineReducers({
  allIds,
  editing,
  conflicts,
  props,
  examples
});

export default reducer;
